import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { FormLabel } from "@impero-com/ui";
import { Control } from "react-hook-form";

import { UserOverview } from "api/generated/model";
import { RHFAccessRights } from "components/form/rhfFields/rhfAccessRights";
import { RHFEditableUserTagListInput } from "components/form/rhfFields/rhfEditableUserTagListPlus";
import { RHFMarkdownInput } from "components/form/rhfFields/rhfMarkdownInput";
import { RHFUserTypeaheadInput } from "components/form/rhfFields/rhfUserTypeaheadInput";
import {
    ControlEntityAccessRightsInstance,
    RiskEntityAccessRightsInstance,
} from "utils/accessRights/models";
import { useAvailableActions } from "utils/hooks/useAvailableActions";

import * as theme from "./ControlEntitiesForm.css";
import {
    ControlEntityFormSchema,
    getNameSchema,
} from "./useControlEntityViewFormSchema";

interface ControlEntitiesFormProps {
    control: Control<ControlEntityFormSchema>;
    users: UserOverview[];
    canEdit: boolean;
    canEditControlAccessRights: boolean;
    canEditRiskAccessRights: boolean;
}

export function ControlEntitiesForm({
    control,
    users,
    canEdit,
    canEditControlAccessRights,
    canEditRiskAccessRights,
}: ControlEntitiesFormProps) {
    const { t } = useTranslation();
    const { userTag } = useAvailableActions();
    const canCreateTag = userTag.create;
    return (
        <div>
            <FormSection
                id={"control-entity-details"}
                titleLabel={t("Entity_Details_Title")}
                withDivider
            >
                <RHFStringInput
                    label={t("Entity_Label_Name")}
                    placeholder={t("Entity_Placeholder_Name")}
                    control={control}
                    name="name"
                    schema={getNameSchema(t)}
                    disabled={!canEdit}
                />
                <RHFMarkdownInput
                    label={t("Entity_Label_Description")}
                    placeholder={t("Entity_Placeholder_Description")}
                    control={control}
                    name="description"
                    disabled={!canEdit}
                />
                <RHFUserTypeaheadInput
                    control={control}
                    name="responsibleId"
                    disabled={false}
                    label={t("Entity_Label_Responsible")}
                />
                <div className={theme.tagsWrapper}>
                    <RHFEditableUserTagListInput
                        cascaderClassName={theme.tagCascader}
                        control={control}
                        name="tagIds"
                        canCreateTag={canCreateTag}
                        disabled={!canEdit}
                        label={t("Entity_Label_Tags")}
                    />
                </div>
            </FormSection>
            <FormSection
                id={"control-entity-access-rights"}
                titleLabel={t("Entity_AccessRights_Title")}
            >
                <FormLabel>{t("Entity_Label_AccessRights")}</FormLabel>
                <RHFAccessRights
                    control={control}
                    users={users}
                    name="controlUserIdAndRights"
                    resourceAccessRights={ControlEntityAccessRightsInstance}
                    disabled={!canEditControlAccessRights}
                />
            </FormSection>
            <FormSection id={"risk-entity-access-rights"}>
                <FormLabel>{"Risk access management"}</FormLabel>
                <RHFAccessRights
                    control={control}
                    users={users}
                    name="riskUserIdAndRights"
                    resourceAccessRights={RiskEntityAccessRightsInstance}
                    disabled={!canEditRiskAccessRights}
                />
            </FormSection>
        </div>
    );
}
